import React, { useMemo, useRef, useState } from "react";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
  FaTimes,
} from "react-icons/fa";

import { API_URL } from "../../config";

const roles = {
  user: "Hausmeister",
  admin: "Administrator",
  superadmin: "Superadministrator",
};

const UserForm = ({
  users = [],
  setUsers,
  currentRole = "user",
  user = null,
  isDemo = false,
  onClose,
  onSaved,
  onBusyChange,
}) => {
  const editing = Boolean(user);
  const requestPending = useRef(false);

  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    email: user?.email || "",
    password: "",
    role: user?.role || "user",
  });

  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const canManage = ["admin", "superadmin"].includes(currentRole);
    const canChooseRole =
    (currentRole === "superadmin" && !isDemo) ||
    (isDemo && currentRole === "admin" && !editing);

  const availableRoles = canChooseRole
    ? isDemo
      ? ["user", "admin"]
      : ["user", "admin", "superadmin"]
    : [form.role];

  const emailExists = users.some(
    (account) =>
      (!editing || String(account.id) !== String(user.id)) &&
      String(account.email || "").trim().toLowerCase() ===
        form.email.trim().toLowerCase()
  );

  const passwordStrength = useMemo(() => {
    let score = 0;

    if (form.password.length >= 10) score++;
    if (form.password.length >= 12) score++;
    if (/[A-Z]/.test(form.password)) score++;
    if (/[a-z]/.test(form.password)) score++;
    if (/\d/.test(form.password)) score++;
    if (/[^A-Za-z0-9]/.test(form.password)) score++;

    if (score <= 2) {
      return { label: "Schwach", level: "weak", width: "33%" };
    }

    if (score <= 4) {
      return { label: "Mittel", level: "medium", width: "66%" };
    }

    return { label: "Stark", level: "strong", width: "100%" };
  }, [form.password]);

  const changeField = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
  };

  const save = async (event) => {
    event.preventDefault();

    if (requestPending.current) return;
    setError("");

    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim().toLowerCase(),
      role: canChooseRole ? form.role : editing ? user.role : "user",
    };

    if (!canManage) {
      setError("Keine Berechtigung zur Benutzerverwaltung.");
      return;
    }

    if (!payload.first_name || !payload.last_name || !payload.email) {
      setError("Bitte alle Felder ausfüllen.");
      return;
    }

    if (emailExists) {
      setError("Diese E-Mail-Adresse ist bereits vergeben.");
      return;
    }

    if (!editing && form.password.length < 10) {
      setError("Das Passwort muss mindestens 10 Zeichen lang sein.");
      return;
    }
    if (
      !editing &&
      new TextEncoder().encode(form.password).length > 72
    ) {
      setError("Das Passwort darf höchstens 72 UTF-8-Bytes enthalten.");
      return;
    }
    if (!editing) payload.password = form.password;

    const token = localStorage.getItem("token");

    if (!token) {
      setError("Bitte melde dich erneut an.");
      return;
    }

    requestPending.current = true;
    setSaving(true);
    onBusyChange?.(true);

    let notice = "";

    try {
      const response = await fetch(
        editing
          ? `${API_URL}/api/users/${user.id}`
          : `${API_URL}/api/users`,
        {
          method: editing ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error || "Der Benutzer konnte nicht gespeichert werden."
        );
      }

      // Nach erfolgreicher Speicherung die aktuellen Serverdaten laden.
      try {
        const refresh = await fetch(`${API_URL}/api/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const accounts = await refresh.json();

        if (!refresh.ok || !Array.isArray(accounts)) {
          throw new Error("Aktualisierung fehlgeschlagen.");
        }

        setUsers?.(accounts);
      } catch {
        notice =
          "Gespeichert, aber die Kontenübersicht konnte nicht " +
          "aktualisiert werden. Bitte lade die Seite neu.";
      }
    } catch (saveError) {
      setError(saveError.message || "Speichern fehlgeschlagen.");
      return;
    } finally {
      requestPending.current = false;
      setSaving(false);
      onBusyChange?.(false);
    }

    onSaved?.({
      type: notice ? "error" : "success",
      text:
        notice ||
        (editing
          ? "Benutzer erfolgreich aktualisiert."
          : "Benutzer erfolgreich angelegt."),
    });
  };
  const deleteUser = async () => {
    if (
      requestPending.current ||
      !editing ||
      !isDemo ||
      currentRole !== "admin" ||
      user.role !== "user"
    ) {
      return;
    }

    const confirmed = window.confirm(
      `Hausmeisterkonto "${user.email}" wirklich löschen?\n\n` +
      "Konten mit Arbeitsdaten oder Protokollverweisen können nicht gelöscht werden."
    );

    if (!confirmed) return;

    setError("");

    const token = localStorage.getItem("token");

    if (!token) {
      setError("Bitte melde dich erneut an.");
      return;
    }

    requestPending.current = true;
    setSaving(true);
    onBusyChange?.(true);

    let deleted = false;
    let notice = "";

    try {
      const response = await fetch(
        `${API_URL}/api/users/${user.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.error || "Benutzer konnte nicht gelöscht werden."
        );
      }

      deleted = true;

      // Das bestätigte Löschen sofort in der Liste anzeigen.
      setUsers?.((previous) =>
        previous.filter(
          (account) => String(account.id) !== String(user.id)
        )
      );

      try {
        const refresh = await fetch(`${API_URL}/api/users`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const accounts = await refresh.json().catch(() => null);

        if (!refresh.ok || !Array.isArray(accounts)) {
          throw new Error("Liste konnte nicht aktualisiert werden.");
        }

        setUsers?.(accounts);
      } catch {
        notice =
          "Benutzer gelöscht. Die Liste konnte nicht vollständig " +
          "aktualisiert werden; bitte lade die Seite neu.";
      }
    } catch (deleteError) {
      setError(
        deleteError instanceof TypeError
          ? "Keine Serverantwort erhalten. Lade die Seite neu, um zu prüfen, ob das Konto gelöscht wurde."
          : deleteError.message
      );
    } finally {
      requestPending.current = false;
      setSaving(false);
      onBusyChange?.(false);
    }

    if (deleted) {
      onSaved?.({
        type: "success",
        text: notice || "Benutzer erfolgreich gelöscht.",
      });
    }
  };
  return (
    <section className="user-form-section">
      <div className="admin-modal-header">
        <div>
          <p className="section-label">Benutzerverwaltung</p>
          <h2 id="user-account-title">
            {editing ? "Benutzer bearbeiten" : "Benutzer hinzufügen"}
          </h2>
        </div>

        <button
          type="button"
          className="admin-modal-close"
          onClick={onClose}
          disabled={saving}
          aria-label="Schließen"
        >
          <FaTimes />
        </button>
      </div>

      {error && (
        <div className="user-form-status user-form-status-error" role="alert">
          <FaExclamationTriangle />
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError("")}
            aria-label="Meldung schließen"
          >
            <FaTimes />
          </button>
        </div>
      )}

      <form className="user-form" onSubmit={save}>
        <div className="user-name-fields">
          <div className="user-edit-field">
            <label htmlFor="account-first-name">Vorname</label>
            <input
              id="account-first-name"
              name="first_name"
              value={form.first_name}
              onChange={changeField}
              maxLength={100}
              autoComplete="given-name"
              disabled={saving}
              autoFocus
              required
            />
          </div>

          <div className="user-edit-field">
            <label htmlFor="account-last-name">Nachname</label>
            <input
              id="account-last-name"
              name="last_name"
              value={form.last_name}
              onChange={changeField}
              maxLength={100}
              autoComplete="family-name"
              disabled={saving}
              required
            />
          </div>
        </div>

        <div className="user-edit-field">
          <label htmlFor="account-email">E-Mail-Adresse</label>
          <input
            id="account-email"
            name="email"
            type="email"
            value={form.email}
            onChange={changeField}
            maxLength={255}
            autoComplete="off"
            disabled={saving}
            required
          />
          {emailExists && (
            <span className="user-field-error">
              Diese E-Mail-Adresse ist bereits vergeben.
            </span>
          )}
        </div>

        {!editing && (
          <div className="user-edit-field">
            <label htmlFor="account-password">Passwort</label>

            <div className="user-account-password">
              <input
                id="account-password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={changeField}
                minLength={10}
                maxLength={72}
                placeholder="Mindestens 10 Zeichen"
                autoComplete="new-password"
                disabled={saving}
                required
              />

              <button
                type="button"
                onClick={() => setShowPassword((previous) => !previous)}
                disabled={saving}
                aria-label={
                  showPassword ? "Passwort verbergen" : "Passwort anzeigen"
                }
                aria-pressed={showPassword}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {form.password && (
              <div className="password-strength">
                <div className="password-strength-header">
                  <span>Passwortstärke</span>
                  <strong>{passwordStrength.label}</strong>
                </div>
                <div className="password-strength-track">
                  <span
                    className={`password-strength-${passwordStrength.level}`}
                    style={{ width: passwordStrength.width }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="user-edit-field">
          <label htmlFor="account-role">Rolle</label>
          <select
            id="account-role"
            name="role"
            value={form.role}
            onChange={changeField}
            disabled={saving || !canChooseRole}
          >
            {availableRoles.map((value) => (
              <option key={value} value={value}>
                {roles[value]}
              </option>
            ))}
          </select>
        </div>

        <div className="user-edit-footer">
          {editing &&
            isDemo &&
            currentRole === "admin" &&
            user.role === "user" && (
              <button
                type="button"
                className="secondary-action"
                onClick={deleteUser}
                disabled={saving}
              >
                Hausmeister löschen
              </button>
            )}
          <button
            type="button"
            className="secondary-action"
            onClick={onClose}
            disabled={saving}
          >
            Abbrechen
          </button>

          <button
            type="submit"
            className="filter-apply-button"
            disabled={saving || emailExists || !canManage}
          >
            <FaCheckCircle />
            {saving
              ? "Wird gespeichert …"
              : editing
                ? "Änderungen speichern"
                : "Benutzer hinzufügen"}
          </button>
        </div>
      </form>
    </section>
  );
};

export default UserForm;